import { useRef, useState } from "react";
import type { ChangeEvent, DragEvent } from "react";

import { useFieldErrors } from "@/ds";
import {
  ANEXO_DROP_TITLE,
  ANEXO_DROP_TITLE_MOBILE,
  ANEXO_TEST_ID_PADRAO,
} from "./anexo-dialog.constants";
import {
  formatarTamanhoAnexo,
  resolverMensagens,
  validarArquivoAnexo,
  validarIdentificacaoAnexo,
} from "./anexo-dialog.validation";
import type { AnexoCampo, AnexoRegras } from "./anexo-dialog.validation";
import type { AnexoDialogProps } from "./types-anexo-dialog";

/**
 * Janela "Adicionar Arquivo": identificação e arquivo (escolhido ou solto) validados em tela
 * conforme as props; só chama `onSalvar` com tudo válido. Cancelar e X não enviam nada.
 */
export function useAnexoDialog({
  title,
  identificacaoLabel,
  identificacaoMax,
  extensoes,
  tamanhoMaxMb,
  dica,
  isMobile = false,
  mensagens,
  testIdPrefix = ANEXO_TEST_ID_PADRAO,
  onSalvar,
  onCancelar,
}: AnexoDialogProps) {
  const regras: AnexoRegras = {
    identificacaoMax,
    extensoes,
    tamanhoMaxMb,
    mensagens: resolverMensagens(mensagens),
  };
  const inputRef = useRef<HTMLInputElement>(null);
  const [identificacao, setIdentificacao] = useState("");
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [arrastando, setArrastando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const { errors, setError, clearError, validate } = useFieldErrors<AnexoCampo>();

  function alterarIdentificacao(valor: string) {
    setIdentificacao(valor);

    const erro = validarIdentificacaoAnexo(valor, regras);
    if (erro) setError("identificacao", erro);
    else clearError("identificacao");
  }

  function receberArquivo(candidato: File | undefined) {
    if (!candidato) return;

    const erro = validarArquivoAnexo(candidato, regras);
    if (erro) {
      setArquivo(null);
      setError("arquivo", erro);
      return;
    }
    setArquivo(candidato);
    clearError("arquivo");
  }

  function aoEscolherArquivo(event: ChangeEvent<HTMLInputElement>) {
    receberArquivo(event.target.files?.[0]);
    // Permite escolher de novo o mesmo arquivo depois de removê-lo.
    event.target.value = "";
  }

  function aoArrastar(event: DragEvent<HTMLElement>) {
    event.preventDefault();
    setArrastando(true);
  }

  function aoSairDoArraste() {
    setArrastando(false);
  }

  function aoSoltar(event: DragEvent<HTMLElement>) {
    event.preventDefault();
    setArrastando(false);
    receberArquivo(event.dataTransfer.files[0]);
  }

  async function salvar() {
    if (enviando) return;
    const regrasDeCampo = {
      identificacao: (valor: unknown) => validarIdentificacaoAnexo(valor, regras),
      arquivo: (valor: unknown) => validarArquivoAnexo(valor, regras),
    };
    if (!validate(regrasDeCampo, { identificacao, arquivo }) || !arquivo) return;

    setEnviando(true);
    try {
      await onSalvar({ identificacao: identificacao.trim(), arquivo });
    } finally {
      setEnviando(false);
    }
  }

  return {
    title,
    identificacaoLabel,
    dica,
    dropTitulo: isMobile ? ANEXO_DROP_TITLE_MOBILE : ANEXO_DROP_TITLE,
    accept: extensoes.map((extensao) => `.${extensao}`).join(","),
    testIds: {
      dialog: `${testIdPrefix}-dialog`,
      cancelar: `${testIdPrefix}-cancel`,
      confirmar: `${testIdPrefix}-confirm`,
      identificacao: `${testIdPrefix}-identificacao`,
      identificacaoInput: `${testIdPrefix}-identificacao-input`,
      drop: `${testIdPrefix}-drop`,
      escolher: `${testIdPrefix}-escolher`,
      arquivoErro: `${testIdPrefix}-arquivo-erro`,
      escolhido: `${testIdPrefix}-escolhido`,
      descartar: `${testIdPrefix}-descartar`,
      input: `${testIdPrefix}-input`,
    },
    inputRef,
    identificacao,
    arquivoNome: arquivo?.name ?? null,
    arquivoTamanho: arquivo ? formatarTamanhoAnexo(arquivo.size) : null,
    arrastando,
    enviando,
    erroIdentificacao: errors.identificacao,
    erroArquivo: errors.arquivo,
    alterarIdentificacao,
    escolherArquivo: () => inputRef.current?.click(),
    aoEscolherArquivo,
    aoArrastar,
    aoSairDoArraste,
    aoSoltar,
    descartarArquivo: () => setArquivo(null),
    salvar,
    cancelar: onCancelar,
  };
}
